import type { OrgKind, UserRole } from '@/lib/api/domains/users';

/*
 * The role chooser's options, derived from the organization's kind (F02 R16,
 * AC-23bf376f): the chooser offers ONLY the roles the organization the account
 * belongs to may hold, and no others. This is the frontend's courtesy half —
 * withholding roles the kind cannot hold; the rule (refusing a role the kind
 * cannot hold on save) is the domain service's role↔org-kind pin. A build that
 * only hides is worse than one that only refuses, so this list must match the
 * domain's matrix exactly:
 *   - a Kelly (TPA) organization holds the single TPA billing role;
 *   - any other (client) organization holds only the standard client role.
 * The Administrator/Viewer distinction is NOT a second role — it is the
 * is_administrator privilege / permission set (glossary "Viewer",
 * "Administrator") — so it is not an option here.
 */

/** One selectable role in the add-user chooser. */
export interface RoleOption {
  /** The role value saved, as the domain fixes it. */
  value: UserRole;
  /** The label shown to the operator. */
  label: string;
}

const ROLE_LABELS: Record<UserRole, string> = {
  tpabilling: 'TPA Billing',
  client: 'Client',
};

/**
 * The roles a given organization kind may hold, in the order they are offered.
 * A Kelly (TPA) org offers only the TPA billing role; a client org offers only
 * the client role. No org kind offers both, and the list is never empty for a
 * known kind — the single-element list is deliberate (AC-23bf376f: "and no
 * others").
 */
export function roleOptionsForOrgKind(orgKind: OrgKind): RoleOption[] {
  const roles: UserRole[] = orgKind === 'tpa' ? ['tpabilling'] : ['client'];
  return roles.map((value) => ({ value, label: ROLE_LABELS[value] }));
}
